
import { defineComponent, PropType, ref, watch } from 'vue'
import { BaseButton } from 'coghent-vue-3-component-library'
import { useI18n } from 'vue-i18n'
import StoryEditDropdown from './StoryEditDropdown.vue'
import { PublicationStatus } from 'coghent-vue-3-component-library'
import { Publication, UserAction } from 'coghent-vue-3-component-library'
import { router } from '@/app'
import { Entity } from 'coghent-vue-3-component-library'
import MyWorksActions from '@/components/MyWorksActions.vue'
import uploadWizard from '@/composables/uploadWizard'

export enum ProfileListItemType {
  story,
  uploadedWork,
  testimony,
}

export type ProfileListItemInfo = {
  id: string
  title: string
  description: string
  dateCreated?: string
  onClickUrl: string
  code?: string
  pictureUrl?: string
  status?: string
  type: ProfileListItemType
  entity?: typeof Entity
  action?: typeof UserAction
}

export type ActionLinks = {
  visit?: string
  edit?: string
  delete?: string
}

export type TagInfo = { title: string; style: string }

export default defineComponent({
  name: 'ProfileListItem',
  components: {
    BaseButton,
    StoryEditDropdown,
    MyWorksActions,
  },
  props: {
    profileListItemInfo: {
      type: Object as PropType<ProfileListItemInfo>,
      required: true,
    },
  },
  emits: ['deleteItem', 'update'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const showStoryFeature = ref<boolean>(false)
    const showWorksFeature = ref<boolean>(false)
    const directDeleteFeature = ref<boolean>(false)
    const isClickable = ref<boolean>(false)
    const openStoryboxes = ref<boolean>(false)
    const assetIsAddedToStoryBox = ref<boolean>(false)
    const links = ref<any>()
    const tagInfo = ref<TagInfo | null>(null)
    const isLoading = ref<boolean>(false)
    const itemAction = ref<typeof UserAction>(props.profileListItemInfo.action)
    const { ASSET_ID_PARAM } = uploadWizard()

    // General use
    const checkCardType = () => {
      if (props.profileListItemInfo.type === ProfileListItemType.story) {
        showStoryFeature.value = true
        isClickable.value = true
        links.value = {
          edit: null,
          visit: props.profileListItemInfo.onClickUrl,
        }
      }
      if (props.profileListItemInfo.type === ProfileListItemType.uploadedWork) {
        showWorksFeature.value = true
        isClickable.value = false
        links.value = {
          edit: `upload?${ASSET_ID_PARAM}=${props.profileListItemInfo.id}`,
          visit:
            tagInfo.value!.title === `In behandeling` && props.profileListItemInfo.id !== undefined
              ? `upload?${ASSET_ID_PARAM}=${props.profileListItemInfo.id}`
              : `/entity/${props.profileListItemInfo.entity.object_id}`,
        }
      }
      if (props.profileListItemInfo.type === ProfileListItemType.testimony) {
        showStoryFeature.value = false
        isClickable.value = true
        directDeleteFeature.value = true
        links.value = {
          edit: null,
          visit: props.profileListItemInfo.onClickUrl,
        }
      }
    }

    // Use for works
    const getTagInfo = () => {
      tagInfo.value = {
        style: '',
        title: '',
      }
      if (props.profileListItemInfo.status) {
        let activeKey = null
        for (const key of Object.values(Publication)) {
          if (props.profileListItemInfo.status === PublicationStatus[key as string]) {
            activeKey = key
          } else if (props.profileListItemInfo.status === 'afgekeurd') {
            activeKey = 'declined'
          }
        }
        switch (activeKey) {
          case Publication.Validate:
            tagInfo.value.title = t('myWorks.overview.statuses.validate')
            tagInfo.value.style = 'bg-accent-orange text-accent-orange'
            break
          case Publication.Public:
            tagInfo.value.title = t('myWorks.overview.statuses.public')
            tagInfo.value.style = 'bg-accent-lightGreen text-accent-lightGreen'
            break
          case Publication.Declined:
            tagInfo.value.title = t('myWorks.overview.statuses.declined')
            tagInfo.value.style = 'bg-text-red text-text-red'
            break
        }

        setCustomWorksTag()
      }
      return tagInfo.value
    }

    const setCustomWorksTag = () => {
      if (itemAction.value === 'deleted') {
        tagInfo.value = {
          title: `Verwijderd`,
          style: `bg-text-red text-text-red`,
        } as TagInfo
      }
    }

    const init = () => {
      if (props.profileListItemInfo.type === ProfileListItemType.uploadedWork) {
        getTagInfo()
      }
      checkCardType()
    }

    watch(tagInfo, () => checkCardType())

    init()

    const deleteEntity = () => {
      emit('deleteItem', props.profileListItemInfo.id)
    }

    const update = () => emit(`update`)

    return {
      t,
      showStoryFeature,
      showWorksFeature,
      directDeleteFeature,
      tagInfo,
      router,
      isClickable,
      links,
      openStoryboxes,
      assetIsAddedToStoryBox,
      deleteEntity,
      isLoading,
      getTagInfo,
      itemAction,
      update,
    }
  },
})
